import { Form, InputNumber, notification, Radio } from 'antd';
import { useSaSmsWrite } from 'hooks/use-sa-sms-api';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

import { SaSmsReasonModal } from './sa-sms-reason-modal';

type Values = { bucket: 'included' | 'purchased'; delta: number };

type Props = { tenantId: string; open: boolean; onClose: () => void; onDone: () => void };

/**
 * Admin credit correction: bucket, signed amount, reason. The request id is minted once per opening and reused by every
 * retry from that dialog, so a double click or a retry after a timeout adjusts at most once (idempotent upstream).
 */
export const SmsCreditAdjustModal: React.FC<Props> = ({ tenantId, open, onClose, onDone }) => {
    const { t } = useTranslation(['sa-sms']);
    const { send } = useSaSmsWrite();
    const requestId = useMemo(() => uuidv4(), [open]);

    const confirm = async ({ reason, bucket, delta }: Values & { reason: string }) => {
        await send('post', `/tenants/${tenantId}/credits/adjust`, {
            bucket,
            delta,
            reason,
            requestId,
        });
        notification.success({ message: t('credits.adjusted') });
        onClose();
        onDone();
    };

    return (
        <SaSmsReasonModal<Values>
            open={open}
            danger
            title={t('credits.adjustTitle')}
            warning={t('credits.adjustWarning')}
            initialValues={{ bucket: 'purchased' }}
            onCancel={onClose}
            onConfirm={confirm}
        >
            <Form.Item name="bucket" label={t('credits.bucket')} rules={[{ required: true }]}>
                <Radio.Group>
                    <Radio value="included">{t('credits.included')}</Radio>
                    <Radio value="purchased">{t('credits.purchased')}</Radio>
                </Radio.Group>
            </Form.Item>
            <Form.Item
                name="delta"
                label={t('credits.delta')}
                extra={t('credits.deltaHint')}
                rules={[
                    { required: true, message: t('credits.deltaRequired') },
                    { type: 'number', min: -10000000, max: 10000000 },
                    {
                        validator: (_, v) =>
                            v === 0
                                ? Promise.reject(new Error(t('credits.deltaZero')))
                                : Promise.resolve(),
                    },
                ]}
            >
                <InputNumber precision={0} style={{ width: '100%' }} />
            </Form.Item>
        </SaSmsReasonModal>
    );
};
